import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { HeroWalker } from '@/components/home/HeroWalker'
import { ContainerAnimated, ContainerStagger } from '@/components/ui/cta-section-with-gallery'
import { useTranslation } from '@/i18n'

/**
 * The homepage's opening: the promise and the two buttons on the left, the
 * walker on the right (below on phones), waving and then pointing at the
 * buttons wherever the layout has put them.
 */
export function Hero() {
  const { t } = useTranslation()
  const buttonsRef = useRef<HTMLDivElement>(null)

  // Light mode opens on a faint indigo wash that fades out, so the hero reads
  // as its own band. Dark mode keeps the flat canvas: --accent there is the
  // card grey.
  return (
    <section className="relative min-h-[100dvh] flex items-center bg-gradient-to-b from-accent/70 to-transparent dark:from-transparent overflow-hidden px-6 sm:px-8 pt-24 pb-16">

      <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-6 sm:gap-12 md:grid-cols-2">
        <ContainerStagger initial="hidden" animate="visible" whileInView={undefined}>
          <ContainerAnimated>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] sm:leading-tight text-foreground tracking-tight text-balance">
              {t.hero.title}
            </h1>
          </ContainerAnimated>
          <ContainerAnimated>
            <p className="my-4 md:my-6 text-lg sm:text-xl text-muted-foreground max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>
          </ContainerAnimated>
          {/* Side by side on phones too, half the width each, so they take one
              row instead of two and the walker's feet stay above the fold. A
              long label (Romanian) wraps inside its button instead. */}
          <ContainerAnimated ref={buttonsRef} className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
            <Link
              to="/contact#booking"
              className="inline-block hover-scale bg-primary text-primary-foreground font-semibold px-3 sm:px-8 py-3 sm:py-3.5 rounded-lg hover:opacity-90 gentle-animation cursor-pointer text-sm sm:text-base leading-tight shadow-lg shadow-primary/20"
            >
              {t.hero.ctaBook}
            </Link>
            <Link
              to="/services"
              className="inline-block hover-scale border-2 border-primary text-primary-text font-semibold px-3 sm:px-8 py-3 sm:py-3.5 rounded-lg hover:bg-primary/5 gentle-animation cursor-pointer text-sm sm:text-base leading-tight"
            >
              {t.hero.ctaServices}
            </Link>
          </ContainerAnimated>
        </ContainerStagger>

        {/* Decorative: the heading already says everything.
            On phones he is pulled up under the buttons: the top of his drawing
            is empty headroom (his raised hand stays well below it), and
            without it his legs fell below the fold. */}
        <ContainerStagger initial="hidden" animate="visible" whileInView={undefined} aria-hidden="true">
          <ContainerAnimated className="-mt-6 sm:mt-0 mx-auto w-full max-w-[280px] sm:max-w-[360px] lg:max-w-[440px]">
            <HeroWalker targetRef={buttonsRef} className="w-full h-auto select-none" />
          </ContainerAnimated>
        </ContainerStagger>
      </div>
    </section>
  )
}
